/**
 * Converts between the grid and today's chunked-RLE checkpoint payload (docs/large-volume-plan.md §3.6).
 *
 * The payload format is unchanged (ADR 0001: no second persistence format): a global palette of
 * `{ t, c, f, tf, m }` items, a chunk size, and per-chunk `[runLength, paletteIndex + 1]` runs over
 * the chunk's full linear volume (0 = empty). `checkpointCodec.ts` owns compression and the outer
 * blob; this module only produces and consumes the inner `{ palette, chunkSize, chunks }` object.
 */
import { isVoxelDensity, isVoxelMaterial, type Face, type VoxelData, type VoxelMaterial } from '@/voxelTypes'
import { parseKeyX, parseKeyY, parseKeyZ, posKeyXYZ, type Position } from '@/voxelMath'
import { ChunkedVoxelGrid } from './ChunkedVoxelGrid'
import { VoxelPalette } from './palette'
import { CHUNK_SHIFT, CHUNK_SIZE, chunkCoords, chunkIdOfChunkXYZ } from './layout'

// tf: topFace (orientation for e.g. hearts). d: SDF density (samples per voxel). Both absent in older checkpoints.
export type PaletteItem = { t: string; c?: number; f?: string; tf?: string; m?: VoxelMaterial; d?: number }
export type ChunkRLE = { origin: [number, number, number]; runs: [number, number][] }
export type GridPayload = { palette: PaletteItem[]; chunkSize: number; chunks: ChunkRLE[] }

export function voxelToPaletteItem(v: VoxelData): PaletteItem {
  return { t: v.type, c: v.color, f: v.face, tf: v.topFace, m: v.material, d: v.density }
}

export function paletteItemToVoxel(p: PaletteItem): VoxelData {
  const voxel: VoxelData = { type: p.t }
  if (typeof p.c === 'number') voxel.color = p.c
  if (p.f) voxel.face = p.f as Face
  if (p.tf) voxel.topFace = p.tf as Face
  if (isVoxelMaterial(p.m)) voxel.material = p.m
  if (isVoxelDensity(p.d)) voxel.density = p.d
  return voxel
}

/**
 * Encodes a grid. Payload palette order is first appearance in encode order; chunks are emitted in
 * chunk-Morton order of first appearance. Chunk sizes that are multiples of 16 take the chunk-wise
 * fast path; other sizes (tests use 4) go cell by cell.
 */
export function encodeGridPayload(g: ChunkedVoxelGrid, chunkSize: number = 32): GridPayload {
  const cs = Math.max(1, Math.floor(chunkSize))
  if (cs % CHUNK_SIZE === 0 && cs <= 512) return encodeAligned(g, cs)
  return encodeGeneric(g, cs)
}

function encodeAligned(g: ChunkedVoxelGrid, cs: number): GridPayload {
  const perAxis = cs >> CHUNK_SHIFT // grid chunks per payload chunk per axis
  const vol = cs * cs * cs
  const palette: PaletteItem[] = []
  const remap = new Int32Array(g.palette.length).fill(-1)
  const items = g.palette.items
  const payloadIndexOf = (id: number): number => {
    let pi = remap[id]
    if (pi < 0) {
      pi = palette.length
      palette.push(voxelToPaletteItem(items[id] as VoxelData))
      remap[id] = pi
    }
    return pi + 1
  }

  // Output chunks in order of first appearance of a non-empty grid chunk (chunk-Morton order).
  const outChunks: number[] = []
  const seen = new Set<number>()
  g.forEachChunk((chunkId, chunk) => {
    if (chunk.count === 0) return
    const [cx, cy, cz] = chunkCoords(chunkId)
    const ox = Math.floor(cx / perAxis), oy = Math.floor(cy / perAxis), oz = Math.floor(cz / perAxis)
    const okey = (ox << 20) | (oy << 10) | oz
    if (!seen.has(okey)) { seen.add(okey); outChunks.push(okey) }
  })

  const buf = new Uint16Array(vol)
  const chunks: ChunkRLE[] = []
  for (const okey of outChunks) {
    const ox = okey >>> 20, oy = (okey >>> 10) & 1023, oz = okey & 1023
    buf.fill(0)
    for (let sz = 0; sz < perAxis; sz++) for (let sy = 0; sy < perAxis; sy++) for (let sx = 0; sx < perAxis; sx++) {
      const chunk = g.getChunk(chunkIdOfChunkXYZ(ox * perAxis + sx, oy * perAxis + sy, oz * perAxis + sz))
      if (chunk === null || chunk.count === 0) continue
      const d = chunk.data
      for (let lz = 0; lz < CHUNK_SIZE; lz++) {
        for (let ly = 0; ly < CHUNK_SIZE; ly++) {
          const src = (lz << 8) | (ly << 4)
          const dst = ((sz * CHUNK_SIZE + lz) * cs + (sy * CHUNK_SIZE + ly)) * cs + sx * CHUNK_SIZE
          buf.set(d.subarray(src, src + CHUNK_SIZE), dst)
        }
      }
    }
    chunks.push({ origin: [ox, oy, oz], runs: rleOf(buf, vol, payloadIndexOf) })
  }
  return { palette, chunkSize: cs, chunks }
}

function encodeGeneric(g: ChunkedVoxelGrid, cs: number): GridPayload {
  const vol = cs * cs * cs
  const palette: PaletteItem[] = []
  const remap = new Int32Array(g.palette.length).fill(-1)
  const items = g.palette.items
  const buckets = new Map<string, { origin: [number, number, number]; cells: Map<number, number> }>()
  g.forEach((_v, key) => {
    const id = g.idAt(key)
    const x = parseKeyX(key), y = parseKeyY(key), z = parseKeyZ(key)
    const cx = Math.floor(x / cs), cy = Math.floor(y / cs), cz = Math.floor(z / cs)
    const lin = (x - cx * cs) + (y - cy * cs) * cs + (z - cz * cs) * cs * cs
    if (remap[id] < 0) { remap[id] = palette.length; palette.push(voxelToPaletteItem(items[id] as VoxelData)) }
    const bk = `${cx},${cy},${cz}`
    let b = buckets.get(bk)
    if (!b) { b = { origin: [cx, cy, cz], cells: new Map() }; buckets.set(bk, b) }
    b.cells.set(lin, remap[id] + 1)
  })
  const chunks: ChunkRLE[] = []
  for (const b of buckets.values()) {
    const runs: [number, number][] = []
    let last = -1, len = 0
    for (let i = 0; i < vol; i++) {
      const v = b.cells.get(i) || 0
      if (i === 0) { last = v; len = 1; continue }
      if (v === last) { len++; continue }
      runs.push([len, last]); last = v; len = 1
    }
    runs.push([len, last])
    chunks.push({ origin: b.origin, runs })
  }
  return { palette, chunkSize: cs, chunks }
}

function rleOf(buf: Uint16Array, vol: number, payloadIndexOf: (id: number) => number): [number, number][] {
  const runs: [number, number][] = []
  let last = buf[0] === 0 ? 0 : payloadIndexOf(buf[0])
  let lastId = buf[0]
  let len = 1
  for (let i = 1; i < vol; i++) {
    const id = buf[i]
    if (id === lastId) { len++; continue }
    runs.push([len, last])
    lastId = id
    last = id === 0 ? 0 : payloadIndexOf(id)
    len = 1
  }
  runs.push([len, last])
  return runs
}

/**
 * Decodes a payload into a frozen grid. A fresh palette is created unless one is supplied; `bounds`
 * clips the grid (keys outside are dropped) and defaults to the whole domain.
 */
export function decodePayloadToGrid(
  obj: GridPayload,
  opts: { bounds?: Readonly<Position> | null; palette?: VoxelPalette } = {},
): ChunkedVoxelGrid {
  const palette = opts.palette ?? new VoxelPalette()
  const g = new ChunkedVoxelGrid(palette, opts.bounds).asMutable()
  const cs = Math.max(1, Math.floor(obj.chunkSize))
  const items = Array.isArray(obj.palette) ? obj.palette : []
  const ids = items.map((p) => (p && typeof p.t === 'string' ? palette.intern(paletteItemToVoxel(p)) : 0))
  const cs2 = cs * cs
  for (const ch of obj.chunks ?? []) {
    const ox = ch.origin[0] * cs, oy = ch.origin[1] * cs, oz = ch.origin[2] * cs
    let i = 0
    for (const run of ch.runs) {
      const len = run[0] | 0
      const val = run[1] | 0
      if (val !== 0 && len > 0) {
        const id = ids[val - 1] ?? 0
        if (id !== 0) {
          let lx = i % cs
          let ly = Math.floor(i / cs) % cs
          let lz = Math.floor(i / cs2)
          for (let k = 0; k < len; k++) {
            g.setId(posKeyXYZ(ox + lx, oy + ly, oz + lz), id)
            if (++lx === cs) { lx = 0; if (++ly === cs) { ly = 0; lz++ } }
          }
        }
      }
      i += len
    }
  }
  return g.asImmutable()
}
